import React, { useEffect, useState } from 'react';
import { BoxV2 as Box, Flex } from 'portal-commons';
import { CampaignDetail, MigrateCnpInfo } from '@/apis/core/types';
import { convertTimeWithTimezone } from '@/utils/time';
import { formatTitleCase } from '../../utils';
import { MIGRATION_STATUS_LABEL_MAP } from '../../constants';

interface Props {
  campaign: CampaignDetail;
  migrationInfo: MigrateCnpInfo;
}

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
  fullWidth?: boolean;
}) => {
  return (
    <Flex
      sx={{
        fontSize: 'H200',
        fontWeight: 'normal',
        color: '#6D6E70',
        lineHeight: '20px',
        flexShrink: 0,
      }}
    >
      <label>{label}:</label>
      <Box sx={{ ml: '6px', color: '#101820', fontSize: 'H300', flex: '1' }}>
        {children ?? 'N/A'}
      </Box>
    </Flex>
  );
};

const Divider = () => (
  <Box
    as="span"
    sx={{
      color: '#6D6E70',
      mx: 'xs', // margin left and right
    }}
  >
    |
  </Box>
);

const CampaignBasicDetailsV2: React.FC<Props> = ({
  campaign,
  migrationInfo,
}) => {
  const [subUsecases, setSubUsecases] = useState<string[]>([]);

  const isDeclined = !!campaign.declineDate;

  const getMigrationStatus = () => {
    const { status = 'No' } = migrationInfo ?? {};
    return MIGRATION_STATUS_LABEL_MAP[status] ?? status;
  };

  const processUsecaseData = () => {
    if (!campaign.subUsecases) return;
    setSubUsecases(campaign.subUsecases.split(','));
  };

  useEffect(() => {
    processUsecaseData();
  }, [campaign]);

  return (
    <Box sx={{ mb: `${isDeclined ? 'xxl' : 'xxxxl'}` }}>
      <Flex sx={{ alignItems: 'baseline', mb: 's' }}>
        <Box
          as="h1"
          sx={{
            fontSize: 'H600',
            fontWeight: 400,
            mr: 's',
            mb: 's',
            color: 't.black80',
          }}
        >
          10DLC CSP Campaign ID:
        </Box>
        <Box
          as="span"
          sx={{ fontSize: '24px', fontWeight: 500, color: 't.blue800' }}
        >
          {campaign?.uid}
        </Box>
      </Flex>
      <Flex
        sx={{
          mb: 'xs',
          color: 't.black60',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Field label="REGISTERED">
          {convertTimeWithTimezone(campaign?.createDate, 'MM/DD/YYYY')}
        </Field>
        <Divider />
        <Field label="USE-CASE">{campaign?.usecase}</Field>
        {subUsecases.length > 0 && (
          <>
            <Divider />
            <Field label="SUB USE-CASES">{subUsecases.join(', ')}</Field>
          </>
        )}
      </Flex>
      <Flex
        sx={{
          color: 't.black60',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 14,
        }}
      >
        <Field label="TCR STATUS">{formatTitleCase(campaign?.status)}</Field>
        <Field label="MIGRATION">{getMigrationStatus()}</Field>
      </Flex>
    </Box>
  );
};

export default CampaignBasicDetailsV2;
